import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { useAuth } from "../../hooks/useAuth";
import {
    LogOut,
    ChevronDown,
    Eye,
    LayoutDashboard,
    Users,
    UserCheck,
    Building2,
    Menu,
    ArrowRightLeft,
    Settings,
    Zap,
    Calendar,
    X,
    BarChart3
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import { getFloorLabel, getFullName } from "../../services/common/util";
import NotificationDropdown from "../notifications/NotificationDropdown";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { fetchTransfers } from "../../services/crmServices/transferService";
import { fetchDesks, fetchAvailableDesks } from "../../services/crmServices/deskService";

const navigationItems = [
    { name: "Dashboard", href: "/", icon: LayoutDashboard },
    {
        name: "Leads", href: "/leads", icon: Users,
        subItems: [
            { name: "Live Leads", href: "/live-leads", adminOnly: true }
        ]
    },
    {
        name: "Callbacks", icon: Calendar,
        subItems: [
            { name: "My Callbacks", href: "/callbacks/my", agentOnly: true },
            { name: "Team Callbacks", href: "/callbacks/team", managerOnly: true },
            { name: "Active Customers", href: "/active-customers", managerOnly: true }
        ]
    },
    { name: "Transfers", href: "/transfers", icon: ArrowRightLeft },
    {
        name: "Management", icon: Building2, managerOnly: true,
        subItems: [
            { name: "Staff", href: "/staff" },
            { name: "Desks", href: "/desks" },
            { name: "Statistics", href: "/statistics", adminOnly: true }
        ]
    },
    {
        name: "Settings", icon: Settings, adminOnly: true,
        subItems: [
            { name: "System Settings", href: "/settings/system" },
            { name: "Lead Status Management", href: "/settings/lead-status" },
            { name: "Custom Position Management", href: "/settings/positions" },
            { name: "Management Actions", href: "/settings/management-actions" }
        ]
    }
];

export function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [pendingTransfersCount, setPendingTransfersCount] = useState(0);

    // ── Pending transfers badge logic ──
    useEffect(() => {
        const fetchPendingTransfers = async () => {
            if (!user?.position?.role || user.position.role === "agent") return;

            try {
                const response = await fetchTransfers();
                if (response?.data?.length) {
                    const pendingTransfers = response.data.filter((t) => t.status === "pending");
                    let relevantTransfers = pendingTransfers;

                    const normalizeDeskId = (desk) => {
                        if (!desk) return null;
                        if (typeof desk === "string") return desk;
                        return (desk._id || desk)?.toString();
                    };

                    if (user.position.role === "deskManager") {
                        const desksResponse = await fetchDesks();
                        const managedDeskIds = (desksResponse?.data || [])
                            .filter((d) => {
                                const mid = d.manager?._id || d.manager;
                                return mid === user._id || mid?.toString() === user._id?.toString();
                            })
                            .map((d) => (d._id?._id || d._id)?.toString())
                            .filter(Boolean);

                        const isManagedDesk = (id) =>
                            id && managedDeskIds.includes(id?.toString());

                        relevantTransfers = pendingTransfers.filter((t) => {
                            return (
                                isManagedDesk(normalizeDeskId(t.lead?.desk)) ||
                                (t.targetDesk && isManagedDesk(normalizeDeskId(t.targetDesk)))
                            );
                        });
                    } else if (user.position.role === "floorManager") {
                        const desksResponse = await fetchDesks();
                        const managedDeskIds = (desksResponse?.data || [])
                            .filter((d) => {
                                const fmId = d.floorManager?._id || d.floorManager;
                                return (
                                    (fmId === user._id || fmId?.toString() === user._id?.toString()) &&
                                    d.floor === "conversion"
                                );
                            })
                            .map((d) => (d._id?._id || d._id)?.toString())
                            .filter(Boolean);

                        const isManagedDesk = (id) =>
                            id && managedDeskIds.includes(id?.toString());

                        relevantTransfers = pendingTransfers.filter((t) => {
                            return (
                                isManagedDesk(normalizeDeskId(t.lead?.desk)) ||
                                (t.targetDesk && isManagedDesk(normalizeDeskId(t.targetDesk)))
                            );
                        });
                    } else if (user.position.role === "generalManager") {
                        const desksResponse = await fetchAvailableDesks();
                        const managedDeskIds = (desksResponse?.data || [])
                            .map((d) => (d._id?._id || d._id)?.toString())
                            .filter(Boolean);

                        const isManagedDesk = (id) =>
                            id && managedDeskIds.includes(id?.toString());

                        relevantTransfers = pendingTransfers.filter((t) => {
                            return (
                                isManagedDesk(normalizeDeskId(t.lead?.desk)) ||
                                (t.targetDesk && isManagedDesk(normalizeDeskId(t.targetDesk)))
                            );
                        });
                    }

                    setPendingTransfersCount(relevantTransfers.length);
                } else {
                    setPendingTransfersCount(0);
                }
            } catch {
                setPendingTransfersCount(0);
            }
        };

        fetchPendingTransfers();
        const interval = setInterval(fetchPendingTransfers, 20000);
        return () => clearInterval(interval);
    }, [user?.position?.role, user?._id]);

    // ── Helpers ──
    const getInitials = (name) =>
        name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);

    const getPositionColor = (position) => {
        if (!position || !position.role) return "bg-gray-100 text-gray-800 border-gray-200";
        switch (position.role) {
            case "admin":
                return "bg-indigo-100 text-indigo-800 border-indigo-200";
            case "floorManager":
                return "bg-amber-100 text-amber-800 border-amber-200";
            case "deskManager":
                return "bg-sky-100 text-sky-800 border-sky-200";
            default:
                return "bg-emerald-100 text-emerald-800 border-emerald-200";
        }
    };

    const filteredNavItems = navigationItems
        .filter((item) => {
            if (!user?.position?.role) return false;
            if (item.adminOnly && user.position.role !== "admin") return false;
            if (item.managerOnly && user.position.role === "agent") return false;
            if (item.agentOnly && user.position.role !== "agent") return false;
            if (user.position.role === "agent" && item.name === "Transfers") return false;
            return true;
        })
        .map((item) => {
            if (!item.subItems) return item;
            const filteredSubs = item.subItems.filter((sub) => {
                if (sub.adminOnly && user.position.role !== "admin") return false;
                if (sub.managerOnly && user.position.role === "agent") return false;
                if (sub.agentOnly && user.position.role !== "agent") return false;
                return true;
            });
            if (filteredSubs.length === 0 && item.href) {
                return { name: item.name, href: item.href, icon: item.icon };
            }
            if (filteredSubs.length === 1 && !item.href) {
                return { name: filteredSubs[0].name, href: filteredSubs[0].href, icon: item.icon };
            }
            return { ...item, subItems: filteredSubs };
        })
        .filter((item) => {
            if (item.subItems && item.subItems.length === 0 && !item.href) return false;
            return true;
        });

    const renderTransferBadge = () => {
        if (pendingTransfersCount === 0) return null;
        return (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                {pendingTransfersCount > 99 ? "99+" : pendingTransfersCount}
            </span>
        );
    };

    // ── Desktop nav item ──
    const NavItem = ({ item }) => {
        const isActive = item.href
            ? location.pathname === item.href
            : item.subItems?.some((sub) => location.pathname === sub.href);
        const Icon = item.icon;

        if (item.subItems && item.subItems.length > 0) {
            const isOpen = openDropdown === item.name;
            return (
                <div
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                >
                    {item.href ? (
                        <Link
                            to={item.href}
                            className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                                isActive
                                    ? "bg-indigo-50 text-indigo-700"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {item.name}
                            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", isOpen && "rotate-180")} />
                        </Link>
                    ) : (
                        <button
                            className={cn(
                                "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                                isActive
                                    ? "bg-indigo-50 text-indigo-700"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            )}
                        >
                            <Icon className="h-4 w-4" />
                            {item.name}
                            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform duration-200", isOpen && "rotate-180")} />
                        </button>
                    )}

                    <div
                        className={cn(
                            "absolute top-full left-0 pt-1 z-50 transition-all duration-200",
                            isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
                        )}
                    >
                        <div className="w-56 bg-white border border-gray-200 rounded-xl shadow-lg shadow-gray-200/50 overflow-hidden">
                            <div className="p-1.5">
                                {item.subItems.map((subItem) => (
                                    <Link
                                        key={subItem.name}
                                        to={subItem.href}
                                        onClick={() => setOpenDropdown(null)}
                                        className={cn(
                                            "flex items-center px-3 py-2.5 text-sm rounded-lg transition-colors duration-150",
                                            location.pathname === subItem.href
                                                ? "bg-indigo-50 text-indigo-700 font-medium"
                                                : "text-gray-700 hover:bg-gray-50"
                                        )}
                                    >
                                        {subItem.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <Link
                to={item.href}
                className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150",
                    isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                )}
            >
                <Icon className="h-4 w-4" />
                {item.name}
                {item.name === "Transfers" && renderTransferBadge()}
            </Link>
        );
    };

    // ── Mobile nav item ──
    const MobileNavItem = ({ item }) => {
        const isActive = item.href
            ? location.pathname === item.href
            : item.subItems?.some((sub) => location.pathname === sub.href);
        const Icon = item.icon;

        if (item.subItems && item.subItems.length > 0) {
            const isOpen = openDropdown === item.name;
            return (
                <div className="space-y-0.5">
                    {item.href ? (
                        <div className="flex items-center">
                            <Link
                                to={item.href}
                                onClick={() => setMobileOpen(false)}
                                className={cn(
                                    "flex items-center gap-2.5 flex-1 px-3 py-2.5 text-sm font-medium rounded-l-lg transition-colors",
                                    isActive ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                                )}
                            >
                                <Icon className="h-4 w-4" />
                                {item.name}
                            </Link>
                            <button
                                onClick={() => setOpenDropdown(isOpen ? null : item.name)}
                                className={cn(
                                    "px-2 py-2.5 rounded-r-lg transition-colors",
                                    isActive ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                                )}
                            >
                                <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} />
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setOpenDropdown(isOpen ? null : item.name)}
                            className={cn(
                                "flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                                isActive ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                            )}
                        >
                            <div className="flex items-center gap-2.5">
                                <Icon className="h-4 w-4" />
                                {item.name}
                            </div>
                            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isOpen && "rotate-180")} />
                        </button>
                    )}

                    {isOpen && (
                        <div className="ml-4 pl-3 border-l-2 border-gray-100 space-y-0.5">
                            {item.subItems.map((subItem) => (
                                <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    onClick={() => setMobileOpen(false)}
                                    className={cn(
                                        "block px-3 py-2 text-sm rounded-lg transition-colors",
                                        location.pathname === subItem.href
                                            ? "bg-indigo-50 text-indigo-700 font-medium"
                                            : "text-gray-600 hover:bg-gray-50"
                                    )}
                                >
                                    {subItem.name}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <Link
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={cn(
                    "flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors",
                    isActive ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"
                )}
            >
                <Icon className="h-4 w-4" />
                {item.name}
                {item.name === "Transfers" && renderTransferBadge()}
            </Link>
        );
    };

    return (
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-sm">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="flex items-center h-14">
                    {/* ─── Logo ─── */}
                    <Link to="/" className="flex items-center gap-2.5 mr-6 shrink-0">
                        <div className="h-8 w-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg flex items-center justify-center shadow-sm shadow-indigo-200">
                            <span className="text-white font-bold text-sm tracking-tight">C</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900 tracking-tight hidden sm:block">CRM</span>
                    </Link>

                    {/* ─── Desktop Navigation ─── */}
                    <nav className="hidden lg:flex items-center gap-0.5 flex-1 min-w-0">
                        {filteredNavItems.map((item) => (
                            <NavItem key={item.name} item={item} />
                        ))}
                    </nav>

                    {/* ─── Spacer ─── */}
                    <div className="flex-1 lg:hidden" />

                    {/* ─── Right Actions ─── */}
                    <div className="flex items-center gap-1">
                        {/* Notifications */}
                        <NotificationDropdown />

                        {/* User Menu */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors duration-150 outline-none">
                                    <Avatar className="h-8 w-8 ring-2 ring-indigo-100">
                                        <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white text-xs font-semibold">
                                            {user?.firstName ? getInitials(getFullName(user)) : "U"}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="hidden md:flex flex-col items-start min-w-0">
                                        <span className="text-sm font-semibold text-gray-900 truncate max-w-[120px] leading-tight">
                                            {getFullName(user)}
                                        </span>
                                        <span className="text-[11px] text-gray-500 leading-tight">
                                            {user?.position?.name || "No Position"}
                                        </span>
                                    </div>
                                    <ChevronDown className="h-3.5 w-3.5 text-gray-400 hidden md:block" />
                                </button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                                align="end"
                                className="w-72 bg-white border border-gray-200 shadow-xl shadow-gray-200/50 rounded-xl p-1.5 mt-1"
                            >
                                {/* User Info Header */}
                                <DropdownMenuLabel className="px-3 py-3">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-11 w-11 ring-2 ring-indigo-100">
                                            <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white font-semibold text-sm">
                                                {user?.firstName ? getInitials(getFullName(user)) : "U"}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                                {getFullName(user)}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                                            <Badge
                                                variant="outline"
                                                className={cn("text-[10px] mt-1 px-1.5 py-0", getPositionColor(user?.position))}
                                            >
                                                {user?.position?.name || "No Position"}
                                            </Badge>
                                        </div>
                                    </div>
                                </DropdownMenuLabel>

                                {/* Quick Info */}
                                {user?.position?.role !== "admin" && (
                                    <div className="px-3 py-2 bg-gray-50/80 rounded-lg mx-1.5 mb-1.5">
                                        {/deskManager|floorManager/.test(user?.position?.role) && (
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <span className="text-gray-400">Floor</span>
                                                    <p className="font-medium text-gray-700">
                                                        {user?.position?.floor
                                                            ? getFloorLabel(user?.position?.floor)
                                                            : "None"}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-gray-400">Desks</span>
                                                    <p className="font-medium text-gray-700">{user?.desksCount || 0}</p>
                                                </div>
                                            </div>
                                        )}
                                        {user?.position?.role === "agent" && (
                                            <div className="grid grid-cols-3 gap-2 text-xs">
                                                <div className="col-span-3">
                                                    <span className="text-gray-400">Floor</span>
                                                    <p className="font-medium text-gray-700">
                                                        {user?.position?.floor
                                                            ? getFloorLabel(user?.position?.floor)
                                                            : "None"}
                                                    </p>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="text-gray-400">Desk</span>
                                                    <p className="font-medium text-gray-700">
                                                        {user?.agentDesk?.name || "None"}
                                                    </p>
                                                </div>
                                                <div>
                                                    <span className="text-gray-400">Leads</span>
                                                    <p className="font-medium text-gray-700">{user?.leadsCount || 0}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <DropdownMenuSeparator className="my-1" />

                                {/* Menu Items */}
                                <DropdownMenuItem
                                    className="cursor-pointer rounded-lg px-3 py-2.5 transition-colors focus:bg-gray-50"
                                    onClick={() => navigate("/profile")}
                                >
                                    <Eye className="h-4 w-4 mr-2.5 text-gray-500" />
                                    <span className="text-sm font-medium">View Profile</span>
                                </DropdownMenuItem>

                                <DropdownMenuSeparator className="my-1" />

                                <DropdownMenuItem
                                    className="cursor-pointer rounded-lg px-3 py-2.5 text-red-600 focus:text-red-600 focus:bg-red-50 transition-colors"
                                    onClick={logout}
                                >
                                    <LogOut className="h-4 w-4 mr-2.5" />
                                    <span className="text-sm font-medium">Sign Out</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Mobile Menu Toggle */}
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="lg:hidden ml-1 h-8 w-8">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-72 p-0">
                                <div className="flex flex-col h-full">
                                    {/* Mobile Header */}
                                    <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-100">
                                        <div className="h-8 w-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg flex items-center justify-center">
                                            <span className="text-white font-bold text-sm">C</span>
                                        </div>
                                        <span className="text-lg font-bold text-gray-900">CRM</span>
                                    </div>

                                    {/* Mobile Nav Items */}
                                    <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
                                        {filteredNavItems.map((item) => (
                                            <MobileNavItem key={item.name} item={item} />
                                        ))}
                                    </nav>

                                    {/* Mobile Footer */}
                                    <div className="p-3 border-t border-gray-100">
                                        <button
                                            onClick={() => { logout(); setMobileOpen(false); }}
                                            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            <LogOut className="h-4 w-4" />
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
}
